import {
  Box,
  Button,
  Center,
  CloseButton,
  Flex,
  FormControl,
  HStack,
  Text,
  Textarea,
  Tooltip,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { useGame } from "contexts/GameContext";
import { useSpeechToText } from "hooks/useSpeechToText";
import { EventTypes, JeopardyStates } from "types";
import { ReactComponent as Microphone } from "./microphone.svg";
import { useEffect, useState, useRef, useCallback } from "react";
import { capitalize } from "lodash";
import { useTimer } from "react-timer-hook";
import { JeopardyTimerBar } from "../shared/JeopardyTimerBar";

const TIME_TO_ANSWER = 30;
const SPEECH_TIP_DISMISSED_KEY = 'jeoparty_speech_tip_dismissed';

// Pulsing animation for when recording
const pulseAnimation = keyframes`
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.15); opacity: 0.8; }
  100% { transform: scale(1); opacity: 1; }
`;

// Check if speech tip was previously dismissed
const wasSpeechTipDismissed = (): boolean => {
  try {
    return localStorage.getItem(SPEECH_TIP_DISMISSED_KEY) === 'true';
  } catch {
    return false;
  }
};

// Mark speech tip as dismissed
const dismissSpeechTip = (): void => {
  try {
    localStorage.setItem(SPEECH_TIP_DISMISSED_KEY, 'true');
  } catch {
    // Ignore localStorage errors
  }
};

export const PlayerAnswering: React.FC = () => {
  const { isBuzzedIn, gameState, submitAnswer, currentPlayer, sendEvent } = useGame();
  const toast = useToast();
  const [answer, setAnswer] = useState("");
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [isChallenging, setIsChallenging] = useState(false);
  const hasStartedTimer = useRef(false);
  const hasShownSupportMessage = useRef(wasSpeechTipDismissed());
  const isTouchActiveRef = useRef(false);
  const isStartingRef = useRef(false);
  const challengeToastIdRef = useRef<string | number | undefined>();

  // Check if player has already used their challenge this round
  const hasUsedChallengeThisRound = gameState?.challengesUsedThisRound?.[currentPlayer?.name || ''] ?? false;

  const handleSpeechError = useCallback((error: string, message: string) => {
    // Don't show toast for 'no-speech' as it's not really an error
    if (error === 'no-speech' || error === 'aborted') return;
    
    toast({
      title: "Speech Recognition Issue",
      description: message,
      status: "warning",
      duration: 5000,
      isClosable: true,
      position: "top",
    });
  }, [toast]);

  const {
    isListening,
    isSupported,
    transcript,
    interimTranscript,
    error,
    errorMessage,
    startListening,
    stopListening,
    resetTranscript,
    browserInfo,
  } = useSpeechToText({
    language: 'en-US',
    onError: handleSpeechError,
  });

  const { seconds, restart } = useTimer({
    expiryTimestamp: new Date(),
    autoStart: false,
    onExpire: () => {
      // Only send time up if we're still buzzed in and not already evaluating
      if (isBuzzedIn && !isEvaluating) {
        sendEvent({ type: EventTypes.BUZZED_IN_TIME_UP });
      }
    },
  });

  // Start timer when player becomes buzzed in
  useEffect(() => {
    if (isBuzzedIn && !hasStartedTimer.current) {
      hasStartedTimer.current = true;
      restart(new Date(new Date().getTime() + TIME_TO_ANSWER * 1000));
    } else if (!isBuzzedIn) {
      hasStartedTimer.current = false;
    }
  }, [isBuzzedIn, restart]);

  // Show support message for Safari/iOS users (once ever, persisted in localStorage)
  useEffect(() => {
    if (browserInfo.supportMessage && !hasShownSupportMessage.current && isBuzzedIn) {
      hasShownSupportMessage.current = true;
      dismissSpeechTip(); // Mark as dismissed immediately so it never shows again
      toast({
        title: "Speech Recognition Tip",
        description: browserInfo.supportMessage,
        status: "info",
        duration: 8000,
        isClosable: true,
        position: "top",
      });
    }
  }, [browserInfo.supportMessage, isBuzzedIn, toast]);

  // Update answer from transcript
  useEffect(() => {
    if (transcript) {
      setAnswer(transcript);
    }
  }, [transcript]);

  // Also show interim transcript for real-time feedback
  useEffect(() => {
    if (interimTranscript && isListening) {
      setAnswer(prev => {
        // If we have a finalized transcript, append interim to it
        if (transcript) {
          return transcript + ' ' + interimTranscript;
        }
        return interimTranscript;
      });
    }
  }, [interimTranscript, isListening, transcript]);

  // Reset answer and transcript when component mounts or when buzzed in changes
  useEffect(() => {
    setAnswer("");
    resetTranscript();
    // Close any existing challenge toast when buzzed in state changes
    if (challengeToastIdRef.current) {
      toast.close(challengeToastIdRef.current);
      challengeToastIdRef.current = undefined;
    }
  }, [isBuzzedIn, resetTranscript, toast]);

  const onSubmit = async () => {
    // Stop listening before submitting
    if (isListening) {
      stopListening();
    }
    
    setIsEvaluating(true);
    try {
      const result = await submitAnswer({
        type: EventTypes.SUBMIT_ANSWER,
        playerName: currentPlayer!.name,
        answer,
      });
      
      // Show toast with feedback if answer was wrong
      const data = (result as { data?: { isCorrect: boolean; reason?: string } } | undefined)?.data;
      if (data && !data.isCorrect) {
        // Show a player-friendly message (the full reason is stored in DB for debugging)
        const friendlyMessage = data.reason?.toLowerCase().includes("not phrased as a question")
          ? "Remember to phrase your answer as a question!"
          : "That's not quite right.";
        
        const canChallenge = !hasUsedChallengeThisRound;
        
        // Show custom toast with challenge button if available
        challengeToastIdRef.current = toast({
          position: "top",
          duration: canChallenge ? null : 4000, // Stay open if challenge available
          isClosable: true,
          render: ({ onClose }) => (
            <Box
              bg="red.600"
              color="white"
              p={4}
              borderRadius="md"
              boxShadow="lg"
              maxW="90vw"
            >
              <HStack justify="space-between" align="flex-start" mb={canChallenge ? 3 : 0}>
                <VStack align="start" spacing={1}>
                  <Text fontWeight="bold" fontSize="lg">Incorrect</Text>
                  <Text fontSize="sm">{friendlyMessage}</Text>
                </VStack>
                <CloseButton size="sm" onClick={onClose} />
              </HStack>
              {canChallenge && (
                <Button
                  colorScheme="orange"
                  size="md"
                  width="100%"
                  onClick={async () => {
                    if (!currentPlayer || isChallenging) return;
                    setIsChallenging(true);
                    try {
                      await sendEvent({
                        type: EventTypes.INITIATE_CHALLENGE,
                        playerName: currentPlayer.name,
                      });
                      onClose();
                      toast({
                        title: "Challenge Initiated",
                        description: "Other players will now vote on your challenge.",
                        status: "info",
                        duration: 3000,
                        isClosable: true,
                        position: "top",
                      });
                    } catch (err) {
                      console.error("Error initiating challenge:", err);
                      toast({
                        title: "Challenge Failed",
                        description: "Unable to initiate challenge.",
                        status: "error",
                        duration: 3000,
                        isClosable: true,
                        position: "top",
                      });
                    } finally {
                      setIsChallenging(false);
                    }
                  }}
                  isLoading={isChallenging}
                >
                  Challenge This Ruling
                </Button>
              )}
            </Box>
          ),
        });
      }
    } catch (error) {
      console.error("Error submitting answer:", error);
      toast({
        title: "Error",
        description: "Failed to submit answer. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    }
    setIsEvaluating(false);
  };

  // Wrapper to handle async startListening without blocking events
  const handleStartListening = useCallback(() => {
    if (isStartingRef.current || isListening) return;
    isStartingRef.current = true;
    startListening().finally(() => {
      isStartingRef.current = false;
    });
  }, [startListening, isListening]);

  // Handle mouse events (desktop)
  const onMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    handleStartListening();
  }, [handleStartListening]);

  const onMouseUp = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    // Small delay to capture final words
    setTimeout(() => {
      stopListening();
    }, 300);
  }, [stopListening]);

  const onMouseLeave = useCallback(() => {
    // Stop if mouse leaves while pressed
    if (isListening && !isTouchActiveRef.current) {
      stopListening();
    }
  }, [isListening, stopListening]);

  // Handle touch events (mobile)
  const onTouchStart = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    isTouchActiveRef.current = true;
    handleStartListening();
  }, [handleStartListening]);

  const onTouchEnd = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    isTouchActiveRef.current = false;
    // Longer delay for mobile to capture final words
    setTimeout(() => {
      stopListening();
    }, 500);
  }, [stopListening]);

  const onTouchCancel = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    isTouchActiveRef.current = false;
    stopListening();
  }, [stopListening]);

  const micSize = isListening ? "10vh" : "5vh";
  const isFinalJeopardy =
    gameState!.currentState === JeopardyStates.MakeFinalJeopardyGuesses;
  const playerHasGuessed = currentPlayer!.finalJeopardyResult !== null;

  // Get tooltip content for microphone button
  const getMicTooltip = () => {
    if (!isSupported) {
      return "Speech recognition is not supported in this browser";
    }
    if (error) {
      return errorMessage || "Speech recognition error";
    }
    if (browserInfo.requiresSiri) {
      return "Hold to speak (Siri must be enabled)";
    }
    return "Hold to speak";
  };

  if (isFinalJeopardy && playerHasGuessed) {
    return (
      <Text p="1em" textAlign="center" textStyle="clue" fontSize="3em">
        Waiting for other players to guess
      </Text>
    );
  }

  return isBuzzedIn || isFinalJeopardy ? (
    <Flex
      width="100%"
      userSelect="none"
      flexDirection="column"
      gap="25px"
      alignItems="center"
      position="relative"
      pt={isFinalJeopardy ? 0 : "80px"}
    >
      {/* Timer bar for buzzed in player (not for final jeopardy) */}
      {isBuzzedIn && !isFinalJeopardy && (
        <Box position="absolute" top="10px" left="50%" transform="translateX(-50%)">
          <JeopardyTimerBar
            secondsRemaining={seconds}
            totalSeconds={TIME_TO_ANSWER}
            size="sm"
            position="relative"
          />
        </Box>
      )}
      <FormControl flexGrow={1} width="80%">
        <Textarea
          fontSize={30}
          height="20vh"
          backgroundColor="white"
          value={capitalize(answer)}
          onChange={(e) => setAnswer(e.target.value)}
          placeholder={isListening ? "Listening..." : "Type or hold mic to speak..."}
          _placeholder={{ color: "gray.400" }}
        />
      </FormControl>
      <Flex flexDirection="column" alignItems="center" gap="20px">
        <Button
          isLoading={isEvaluating}
          onClick={onSubmit}
          backgroundColor="jeopardy.lightBlue"
        >
          <Text textStyle="clue">Submit</Text>
        </Button>
        {/* Show microphone for regular clues, not final jeopardy */}
        {!isFinalJeopardy && (
          <Tooltip 
            label={getMicTooltip()} 
            placement="top"
            hasArrow
            isDisabled={isListening}
          >
            <Center
              onMouseDown={isSupported ? onMouseDown : undefined}
              onMouseUp={isSupported ? onMouseUp : undefined}
              onMouseLeave={isSupported ? onMouseLeave : undefined}
              onTouchStart={isSupported ? onTouchStart : undefined}
              onTouchEnd={isSupported ? onTouchEnd : undefined}
              onTouchCancel={isSupported ? onTouchCancel : undefined}
              height="12vh"
              width="12vh"
              cursor={isSupported ? "pointer" : "not-allowed"}
              opacity={isSupported ? 1 : 0.4}
              position="relative"
              role="button"
              aria-label={isListening ? "Recording - release to stop" : "Hold to speak"}
              tabIndex={0}
              // Prevent context menu on long press (mobile)
              onContextMenu={(e) => e.preventDefault()}
              // Add touch-action to prevent scrolling while holding
              sx={{ touchAction: 'none' }}
            >
              {/* Pulsing background when listening */}
              {isListening && (
                <Box
                  position="absolute"
                  width="100%"
                  height="100%"
                  borderRadius="50%"
                  backgroundColor="rgba(255, 0, 0, 0.3)"
                  animation={`${pulseAnimation} 1s ease-in-out infinite`}
                />
              )}
              <Microphone 
                fill={isListening ? "#ff4444" : error ? "#888888" : "white"} 
                height={micSize} 
                width={micSize}
                style={{
                  transition: 'all 0.2s ease',
                  position: 'relative',
                  zIndex: 1,
                }}
              />
            </Center>
          </Tooltip>
        )}
        {/* Show recording indicator text */}
        {isListening && (
          <Text textStyle="clue" color="red.400" fontSize="sm">
            Recording... Release to stop
          </Text>
        )}
        {/* Show helpful tip if speech isn't working */}
        {!isSupported && (
          <Text textStyle="clue" color="gray.400" fontSize="xs" textAlign="center" px={4}>
            Speech input unavailable. Please type your answer.
          </Text>
        )}
      </Flex>
    </Flex>
  ) : (
    <Flex flexDirection="column" alignItems="center" gap="20px" p={4}>
      <Text textStyle="clue">{gameState!.buzzedInPlayer!.name} is buzzed in</Text>
      <Box
        bg="jeopardy.clueBlue"
        borderRadius="lg"
        p={6}
        width="90%"
        maxWidth="500px"
      >
        <Text textStyle="clue" fontSize={{ base: "lg", md: "xl" }} textAlign="center">
          {gameState!.activeClue?.question}
        </Text>
      </Box>
    </Flex>
  );
};
